import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { widget } from './types';
import GridItem from '../GridItem';
import Customizer from '../customizer';
import { Box, Stack } from '@mui/material';
import { AlertCircleOutline } from 'mdi-material-ui';
import FallbackSpinner from 'src/@vigo/vigo-spinner';
import ComponentEditDrawer from '../ComponentEditDrawer';
import { useSettings } from 'src/@core/hooks/useSettings';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { Responsive as GridLayout } from 'react-grid-layout';
import { DashboardObjectDto } from '../../../models/account';
import { useDashboardQueries } from './helpers/useDashboardQueries';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { useDashboardMutations } from './helpers/useDashboardMutations';
import { useAppDispatch } from 'src/store/baseHooks';
import { setItemHeaderTitle } from 'src/store/slices/uiFlowSlice';

import 'react-resizable/css/styles.css';
import 'react-grid-layout/css/styles.css';
import css from './builder.module.css';

import { useRegisterActiveTabFunctions } from 'src/@core/hooks/useRegisterActiveTabFunctions';
import DashModeWatcher from '../DashModeWatcher';
import { useStateWithRef } from 'src/@core/hooks/useStateWithRef';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';

export type DashboardViewMode = 'view' | 'edit';

interface DashboardVersionTwoProps {
  dashboardId: string;
  dateFilterStrings?: string[][];
  orderByValue?: string;
  viewMode?: DashboardViewMode;
}

const DashboardVersionTwo: React.FC<DashboardVersionTwoProps> = ({
  dashboardId,
  dateFilterStrings,
  orderByValue,
  viewMode = 'view'
}) => {
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState<boolean>(false);
  const [customizerDrawerOpen, setCustomizerDrawerOpen] = useState<boolean>(false);
  const [selectedWidget, setSelectedWidget] = useState<widget | undefined>(undefined);
  const [containerWidth, setContainerWidth] = useState<number>(1340);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dashboardPlayground, setDashboardPlayground, dashboardPlaygroundRef] = useStateWithRef<
    DashboardObjectDto | undefined
  >(undefined);

  const level = useGetProductLevel();
  const { settings } = useSettings();
  const dispatch = useAppDispatch();
  const { vigoToast } = useVigoToast({});

  const { dashboardQueryKey, dashboardData, dashboardWidgets, isLoading, refetchDashboard } =
    useDashboardQueries(dashboardId);

  const { saveDashboard } = useDashboardMutations();

  useRegisterActiveTabFunctions({ save: save }, dashboardId);

  const isEditMode = viewMode === 'edit';
  const showCustomizer = isEditMode && (level === Products.Quantum || level === Products.Flow);

  function save(onSuccess?: () => void | undefined) {
    const newDashboard = dashboardPlaygroundRef.current;
    if (!newDashboard) return;

    return vigoToast.promise(
      'Saving dashboard layout ...',
      'Dashboard layout saved',
      'Error saving dashboard layout',
      true,
      saveDashboard(newDashboard as DashboardObjectDto, dashboardQueryKey, dashboardId, refetchDashboard, onSuccess)
    );
  }

  /**
   * Handle removing a dashboard component.
   *
   * @param key string
   * @returns void
   */
  const handleDelete = (key: string): void => {
    if (!dashboardPlayground || !dashboardPlayground?.dashboardComponents || !dashboardQueryKey) return;

    const widgetIndex: number = dashboardPlayground?.dashboardComponents.findIndex(
      (component: any) => component.i === key
    );

    const widget = dashboardPlayground?.dashboardComponents[widgetIndex];

    let newDashboard = undefined;

    if (widgetIndex >= 0) {
      if (Object.hasOwn(widget, 'version')) {
        dashboardPlayground.dashboardComponents[widgetIndex].isDeleted = true;

        newDashboard = {
          ...dashboardPlayground
        };
      } else {
        newDashboard = {
          ...dashboardPlayground,
          dashboardComponents: [
            ...dashboardPlayground.dashboardComponents.slice(0, widgetIndex),
            ...dashboardPlayground.dashboardComponents.slice(widgetIndex + 1)
          ]
        };
      }

      setDashboardPlayground(newDashboard);
    }
  };

  /**
   * Handle static property of dashboard component.
   * - Currently does not save as no property exists in database.
   *
   * @param key string
   * @returns void
   */
  const handleToggleStatic = (key: string): void => {
    if (!dashboardPlayground || !dashboardPlayground?.dashboardComponents || !dashboardQueryKey) return;

    const widgetIndex: number = (dashboardPlayground?.dashboardComponents ?? []).findIndex(
      (component: any) => component?.i === key
    );

    if (widgetIndex >= 0) {
      const tempWidget: any = { ...(dashboardPlayground?.dashboardComponents ?? [])[widgetIndex] };

      if (tempWidget) {
        tempWidget.static = !tempWidget.static;

        setDashboardPlayground(old => ({
          ...old,
          dashboardComponents: [
            ...(old?.dashboardComponents ?? []).slice(0, widgetIndex),
            tempWidget,
            ...(old?.dashboardComponents ?? []).slice(widgetIndex + 1)
          ]
        }));
      }
    }
  };

  /**
   * Open the settings drawer for a dashboard component.
   * - Currently does not save anything, needs development.
   * @param key string
   * @returns void
   */
  const handleOpenSettings = (key: string): void => {
    const widget = (dashboardPlayground?.dashboardComponents ?? []).find((component: any) => component?.i == key);

    if (widget) {
      setSelectedWidget(widget as widget);
      setSettingsDrawerOpen(true);
    }
  };

  /**
   * Handle dropping a draggable onto the dashboard.
   *
   * @param layout
   * @param layoutItem
   * @param event
   * @returns void
   */
  const onDrop = useCallback(
    (_layout: any, layoutItem: any, event: any): void => {
      if (!dashboardData) return;

      const { id, type, title, dimensions, isResizeable, dashboardWidgetId } = JSON.parse(
        event.dataTransfer.getData('object')
      );

      const newWidget = {
        i: id,
        x: layoutItem.x,
        y: layoutItem.y,
        w: dimensions.w,
        h: dimensions.h,
        minW: dimensions.minW,
        minH: dimensions.minH,
        maxW: dimensions.maxW,
        maxH: dimensions.maxH,
        type: type,
        title,
        isBound: true,
        static: false,
        isResizeable: isResizeable,
        dashboardWidgetId
      };

      const newDashboard = {
        ...dashboardPlayground,
        dashboardComponents: [...(dashboardPlayground?.dashboardComponents ?? []), newWidget]
      };

      setDashboardPlayground(newDashboard as DashboardObjectDto);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardPlayground]
  );

  /**
   * Handle the onDropDragOver event.
   *
   * @param _rest
   * @returns void
   */
  const onDropDragOver = useCallback(
    (..._rest: any[]): void => {
      customizerDrawerOpen && setCustomizerDrawerOpen(false);
      settingsDrawerOpen && setSettingsDrawerOpen(false);
    },
    [customizerDrawerOpen, settingsDrawerOpen]
  );

  /**
   * Handle changes events in the dashboard.
   * - Is used for onResizeStop and onDragStop events.
   *
   * @param layoutItems
   * @returns void
   */
  const onLayoutChange = useCallback(
    (layoutItems: any): void => {
      if (!dashboardData || !dashboardPlayground?.dashboardComponents || !dashboardQueryKey) return;

      const tempArray: any[] = [...dashboardPlayground.dashboardComponents];

      layoutItems?.forEach((item: any) => {
        const index = tempArray.findIndex(tempItem => tempItem.i == item.i);
        if (index >= 0) {
          tempArray[index].x = item.x;
          tempArray[index].y = item.y;
          tempArray[index].w = item.w;
          tempArray[index].h = item.h;
          tempArray[index].isBound = true;
        }
      });

      const newDashboard = { ...dashboardPlayground, dashboardComponents: tempArray };

      setDashboardPlayground(newDashboard);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardData, dashboardPlayground, dashboardQueryKey]
  );

  /**
   * Build the dashboard components from the dashboard data.
   *
   * @param {widget: widget}
   * @returns JSX.Element
   */
  const buildComponent = ({ widget }: { widget: widget }): JSX.Element => {
    return (
      <div
        key={widget.i}
        className={`${css.reactGridItem}`}
        data-grid={{
          x: widget?.x,
          y: widget?.y,
          w: widget?.w,
          h: widget?.h,
          i: widget.i,
          static: widget.static,
          // isResizable: isEditMode && widget?.isResizeable, -- Override to always be resizable until control implemented with back end.
          // resizeHandles: [isEditMode && widget?.isResizeable ? 'se' : ''],
          isResizable: Boolean(settings?.unlockDashboardLayout),
          resizeHandles: ['se'],
          minW: 1,
          minH: 1
        }}>
        <GridItem
          openSettings={handleOpenSettings}
          {...{
            widget,
            handleToggleStatic,
            handleDelete,
            orderByValue,
            isEditMode,
            dashboardDateStrings: dateFilterStrings
          }}
        />
      </div>
    );
  };

  /**
   * Memoized array of dashboard components.
   *
   * - Helps to improve rendering performance
   */
  const ChildrenArray = React.useMemo(() => {
    // if (!dashboardData?.dashboardComponents) return [];
    if (!dashboardPlayground?.dashboardComponents) return [];

    const componentsArray = dashboardPlayground.dashboardComponents
      .filter(component => !component?.isDeleted)
      .map((component: any) => ({
        ...component
      }));

    return componentsArray?.map(widget => {
      return buildComponent({ widget });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardPlayground, settings.unlockDashboardLayout, dateFilterStrings, orderByValue]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    
return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  useEffect(() => {
    setDashboardPlayground(dashboardData);
    dispatch(setItemHeaderTitle(''));
    dispatch(setItemHeaderTitle(dashboardData?.name ?? ''));

    return () => {
      dispatch(setItemHeaderTitle(''));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  if (isLoading) {
    return (
      <Box>
        <FallbackSpinner name="customDashboardSpinner" />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'auto'
      }}>
      <DashModeWatcher viewMode={viewMode} />
      {!dashboardData && !isLoading && (
        <Stack direction="row" gap="5px" padding="30px" alignItems="center">
          <AlertCircleOutline />
          <VigoTypography sx={{ fontSize: '1.2rem', fontWeight: '600' }} value="No dashboard available" />
        </Stack>
      )}

      {!settings.unlockDashboardLayout &&
        dashboardData &&
        !isLoading &&
        dashboardPlayground?.dashboardComponents?.length === 0 && (
          <Stack direction="row" gap="5px" padding="30px" alignItems="center">
            <AlertCircleOutline />
            <VigoTypography
              sx={{ fontSize: '1.2rem', fontWeight: '600' }}
              value="Drag components here to build a dashboard"
            />
          </Stack>
        )}

      <GridLayout
        className={`layout ${css.layout} ${settings.unlockDashboardLayout ? css.hideHandles : ''}`}
        draggableCancel=".overlayActionsContainer"
        // autoSize={true}
        width={containerWidth}
        compactType={settings.compactType}
        allowOverlap={settings.allowOverlap}
        isDroppable={settings.unlockDashboardLayout}
        isDraggable={settings.unlockDashboardLayout}
        isResizable={settings.unlockDashboardLayout}
        // isBounded={true}
        rowHeight={138}
        layouts={{ lg: dashboardData?.dashboardComponents }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 1, xs: 1, xxs: 1 }}
        onDrop={onDrop}
        onDropDragOver={onDropDragOver}
        onDragStop={onLayoutChange}
        onResizeStop={onLayoutChange}
        droppingItem={{ i: '0', w: 2, h: 2 }}
        useCssTransforms={true}
        margin={{
          lg: [20, 20],
          md: [20, 20],
          sm: [20, 20],
          xs: [20, 20],
          xxs: [20, 20]
        }}>
        {ChildrenArray}
      </GridLayout>

      <ComponentEditDrawer setOpen={setSettingsDrawerOpen} open={settingsDrawerOpen} widget={selectedWidget} />

      {showCustomizer && (
        <Customizer open={customizerDrawerOpen} setOpen={setCustomizerDrawerOpen} components={dashboardWidgets ?? []} />
      )}
    </Box>
  );
};

export default DashboardVersionTwo;
